import type { ModuleConfig } from './index'

/** Ein eingesammeltes Manifest: der Pfad der Datei und ihr Default-Export. */
export interface ModuleSource {
  /** Pfad der Manifestdatei, so wie ihn `import.meta.glob` liefert. */
  path: string
  /** Der Default-Export der Manifestdatei — hier bewusst noch ungeprüft. */
  config: unknown
}

/** Fehler mit allen gefundenen Problemen der Modul-Manifeste. */
export class ModuleManifestError extends Error {
  readonly problems: string[]

  constructor(problems: string[]) {
    super(formatProblems(problems))
    this.name = 'ModuleManifestError'
    this.problems = problems
  }
}

/**
 * Leitet die Modul-ID aus dem Pfad der Manifestdatei ab: der Ordner direkt
 * unter `modules/`.
 *
 * Ein Modul ist ein Ordner, der Frontend und Backend enthält — das Manifest
 * liegt deshalb in `modules/<id>/frontend/`, nicht direkt in `modules/<id>/`.
 * Die ID ist die Ebene darüber.
 *
 * @example
 * ```ts
 * moduleIdFromPath('../../../../modules/wartung/frontend/module.config.ts') // 'wartung'
 * ```
 */
export function moduleIdFromPath(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/').filter(Boolean)

  // Der Normalfall: irgendwo im Pfad steht modules/, danach kommt die ID.
  const modulesIndex = parts.lastIndexOf('modules')
  const afterModules = modulesIndex === -1 ? undefined : parts[modulesIndex + 1]
  if (afterModules !== undefined && !afterModules.endsWith('.ts')) {
    return afterModules
  }

  // Rückfall für Pfade ohne modules/-Segment (z.B. in Tests): der Ordner der
  // Manifestdatei, die frontend/-Ebene übersprungen.
  const parent = parts[parts.length - 2]
  if (parent === 'frontend') return parts[parts.length - 3] ?? ''
  return parent ?? ''
}

/**
 * Prüft alle eingesammelten Manifeste und gibt sie typisiert zurück.
 * Wirft einen {@link ModuleManifestError} mit einer Klartext-Meldung, sobald
 * auch nur ein Manifest fehlerhaft ist — lieber ein deutlicher Abbruch beim
 * Start als ein Modul, das später still nicht auftaucht.
 *
 * @example
 * ```ts
 * const found = import.meta.glob('../../../../modules/*\/frontend/module.config.ts', { eager: true })
 * const modules = validateModules(
 *   Object.entries(found).map(([path, mod]) => ({ path, config: mod.default })),
 * )
 * ```
 */
export function validateModules(sources: ModuleSource[]): ModuleConfig[] {
  const problems: string[] = []
  const valid: ModuleConfig[] = []
  const seen = new Map<string, string>()
  const mitScopes: string[] = []

  for (const source of sources) {
    const expectedId = moduleIdFromPath(source.path)
    const found: string[] = []
    const config = source.config

    if (typeof config !== 'object' || config === null) {
      problems.push(
        problem(source.path, [
          'Die Datei exportiert kein Manifest als Default-Export.',
          'Erwartet wird: export default defineModule({ ... })',
        ]),
      )
      continue
    }

    const raw = config as Record<string, unknown>

    checkString(raw, 'id', found, `id: '${expectedId}'`)
    checkString(raw, 'title', found, "title: 'Wartung'")
    checkString(raw, 'description', found, "description: 'Wartungsaufträge planen.'")
    checkString(raw, 'icon', found, "icon: 'Wrench'  (Name eines lucide-react Icons)")
    checkNumber(raw, 'order', found, 'order: 10')
    checkString(raw, 'apiBasePath', found, `apiBasePath: '/api/${expectedId}'`)
    checkStringArray(raw, 'roles', found, 'roles: []')
    checkFunction(raw, 'routes', found, "routes: () => import('./src/routes')")

    if (raw.homeWidget !== undefined && typeof raw.homeWidget !== 'function') {
      found.push(
        'Feld "homeWidget" muss eine Funktion sein, die einen dynamischen Import zurückgibt.\n' +
          "   Richtig: homeWidget: () => import('./src/home-widget')",
      )
    }
    if (raw.enabled !== undefined && typeof raw.enabled !== 'boolean') {
      found.push(
        'Feld "enabled" muss true oder false sein (oder ganz weggelassen werden).',
      )
    }

    if (typeof raw.id === 'string' && raw.id !== expectedId) {
      found.push(
        `Die id "${raw.id}" passt nicht zum Ordnernamen "${expectedId}".\n` +
          `   Die id muss exakt dem Ordner unter modules/ entsprechen (also modules/${expectedId}/,\n` +
          `   nicht dem frontend/-Unterordner) — sie wird zum URL-Pfad und zum Schema in der Datenbank.\n` +
          `   Richtig: id: '${expectedId}'`,
      )
    }

    if (typeof raw.apiBasePath === 'string' && raw.apiBasePath !== `/api/${expectedId}`) {
      found.push(
        `Der apiBasePath "${raw.apiBasePath}" entspricht nicht der Konvention.\n` +
          `   Jede Modul-API liegt unter /api/<id>. Daraus bauen Shell, Dev-Proxy und\n` +
          `   Reverse Proxy ihre Regeln automatisch.\n` +
          `   Richtig: apiBasePath: '/api/${expectedId}'`,
      )
    }

    if (typeof raw.id === 'string' && raw.id.length > 0) {
      if (!/^[a-z][a-z0-9-]*$/.test(raw.id)) {
        found.push(
          `Die id "${raw.id}" ist nicht zulässig.\n` +
            '   Erlaubt sind Kleinbuchstaben, Ziffern und Bindestriche, beginnend mit einem Buchstaben.',
        )
      }
      const other = seen.get(raw.id)
      if (other) {
        found.push(
          `Die id "${raw.id}" wird bereits von ${other} verwendet.\n` +
            '   Jede Modul-ID darf es nur einmal geben. Benennen Sie Ihren Ordner und die id um.',
        )
      } else {
        seen.set(raw.id, source.path)
      }
    }

    // Absichtlich kein Fehler, sondern ein Hinweis: ein übrig gebliebenes
    // scopes-Feld schadet nicht, es wird schlicht nicht mehr gelesen. Würde es
    // die Prüfung rotmachen, nähme ein einzelnes altes Manifest das ganze
    // Portal mit — und zwar für alle Module gleichzeitig.
    if (Array.isArray(raw.scopes) && raw.scopes.length > 0) {
      mitScopes.push(typeof raw.id === 'string' ? raw.id : source.path)
    }

    if (raw.surfaces !== undefined) {
      if (
        !raw.surfaces ||
        typeof raw.surfaces !== 'object' ||
        Array.isArray(raw.surfaces)
      ) {
        found.push('surfaces muss eine Zuordnung benannter Erfassungsbereiche sein.')
      } else {
        for (const [id, definition] of Object.entries(raw.surfaces)) {
          const surface = definition as Record<string, unknown> | null
          if (
            !/^[a-z][a-z0-9-]*-v[1-9][0-9]*$/.test(id) ||
            !surface ||
            typeof surface.title !== 'string' ||
            !surface.title.trim() ||
            typeof surface.load !== 'function' ||
            (surface.access !== undefined &&
              surface.access !== 'module' &&
              surface.access !== 'contextual')
          ) {
            found.push(
              `Erfassungsbereich "${id}" benötigt eine versionierte Kennung, title, load und einen gültigen access-Wert.`,
            )
          }
        }
      }
    }

    if (found.length > 0) {
      problems.push(problem(source.path, found))
    } else {
      valid.push(raw as unknown as ModuleConfig)
    }
  }

  if (problems.length > 0) {
    throw new ModuleManifestError(problems)
  }

  if (mitScopes.length > 0) {
    console.warn(
      [
        `Diese Module tragen noch ein Feld "scopes": ${mitScopes.join(', ')}.`,
        '',
        'Der Wert wird nicht verwendet. Es gibt eine App-Registrierung für die',
        'gesamte Plattform-API, und welcher Bereich das ist, hängt an der Umgebung —',
        'in Test ein anderer als in Produktion. Deshalb kommt der Scope aus der',
        'Laufzeitkonfiguration und nicht aus dem Manifest.',
        '',
        'So geht es weiter: die Zeile scopes: [...] aus module.config.ts entfernen.',
      ].join('\n'),
    )
  }

  return valid
}

function checkString(
  raw: Record<string, unknown>,
  field: string,
  found: string[],
  example: string,
): void {
  const value = raw[field]
  if (typeof value !== 'string' || value.trim() === '') {
    found.push(`Feld "${field}" fehlt oder ist leer.\n   Richtig: ${example}`)
  }
}

function checkNumber(
  raw: Record<string, unknown>,
  field: string,
  found: string[],
  example: string,
): void {
  const value = raw[field]
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    found.push(`Feld "${field}" fehlt oder ist keine Zahl.\n   Richtig: ${example}`)
  }
}

function checkStringArray(
  raw: Record<string, unknown>,
  field: string,
  found: string[],
  example: string,
): void {
  const value = raw[field]
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    found.push(
      `Feld "${field}" fehlt oder ist keine Liste von Texten.\n   Richtig: ${example}`,
    )
  }
}

function checkFunction(
  raw: Record<string, unknown>,
  field: string,
  found: string[],
  example: string,
): void {
  if (typeof raw[field] !== 'function') {
    found.push(`Feld "${field}" fehlt oder ist keine Funktion.\n   Richtig: ${example}`)
  }
}

function problem(path: string, entries: string[]): string {
  const file = path.replace(/\\/g, '/').replace(/^(\.\.\/)+/, '')
  return `${file}\n${entries.map((entry) => `   - ${entry}`).join('\n')}`
}

function formatProblems(problems: string[]): string {
  return [
    '',
    'Die Shell konnte nicht starten: mindestens ein Modul-Manifest ist fehlerhaft.',
    '',
    ...problems.map((entry, index) => `${index + 1}) ${entry}\n`),
    'Beheben Sie die Punkte in der genannten module.config.ts und laden Sie die Seite neu.',
    'Wenn Sie nicht weiterkommen, wenden Sie sich an den Plattform-Owner.',
    '',
  ].join('\n')
}
